import { useState } from 'react';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogModal } from '@/components/DialogModal';

import { AlertTriangle, Mail, Copy, Check } from 'lucide-react';

import { EMAIL_WARNING_DISMISSED_KEY } from '@/hooks/useClientInvite';

interface EmailDeliverabilityWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConnectEmail: () => void;
  onSendAnyway: () => void;
  intakeFormLink: string;
  emailTextContent: string;
}

export function EmailDeliverabilityWarningModal({
  open,
  onClose,
  onConnectEmail,
  onSendAnyway,
  intakeFormLink,
  emailTextContent,
}: EmailDeliverabilityWarningModalProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  function handleClose() {
    if (dontShowAgain) {
      localStorage.setItem(EMAIL_WARNING_DISMISSED_KEY, 'true');
    }
    onClose();
  }

  function handleSendAnyway() {
    if (dontShowAgain) {
      localStorage.setItem(EMAIL_WARNING_DISMISSED_KEY, 'true');
    }
    onSendAnyway();
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(intakeFormLink);

      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }

  async function handleCopyEmail() {
    try {
      await navigator.clipboard.writeText(emailTextContent);

      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  }

  return (
    <DialogModal
      open={open}
      onClose={handleClose}
      title="Email Deliverability Warning"
      width="lg"
      subTitle=""
      primaryButton={
        <div className="flex w-full flex-col gap-3 sm:flex-row-reverse">
          <Button
            onClick={handleSendAnyway}
            variant="outline"
          >
            Send Anyway
          </Button>
        </div>
      }
    >
      <div className="my-5 space-y-6">
        <div className="flex items-start gap-3 rounded-lg bg-yellow-50 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-yellow-600" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-900">Your email may end up in spam</h3>
            <p className="mb-4 mt-1 text-sm text-yellow-800">
              Without a connected email account, invitations are sent from a generic system email
              address. This significantly increases the chance of your email being marked as spam by
              email providers.
            </p>
            <Button
              onClick={onConnectEmail}
              className="w-full"
            >
              <Mail className="size-4" />
              Connect Email Now
            </Button>
          </div>
        </div>

        <Accordion
          type="single"
          collapsible
          className="w-full"
        >
          <AccordionItem
            value="alternative-method"
            className="rounded-lg border border-gray-200 bg-gray-100"
          >
            <AccordionTrigger className="text-sm text-gray-950">
              Alternatively, send it manually
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 p-4">
              <p className="text-sm text-gray-700">
                Copy the intake form link or email text below and send it manually.
              </p>

              <div>
                <div className="mb-2 flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Email Template</label>
                  <Button
                    onClick={handleCopyEmail}
                    size="icon"
                    className="size-6"
                    variant="link"
                  >
                    {copiedEmail ? <Check className="size-4" /> : <Copy className="size-4" />}
                  </Button>
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    size="sm"
                    className="ml-auto h-[30px] bg-transparent p-0 px-2 text-xs"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="!size-3.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="!size-3.5" />
                        Copy Intake Form Link
                      </>
                    )}
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-300 bg-white p-3 text-sm text-gray-800">
                  {emailTextContent}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="flex items-center gap-2">
          <Checkbox
            id="dontShowAgain"
            checked={dontShowAgain}
            onCheckedChange={checked => setDontShowAgain(checked as boolean)}
          />
          <label
            htmlFor="dontShowAgain"
            className="cursor-pointer text-sm text-gray-700"
          >
            Don't show this warning again
          </label>
        </div>
      </div>
    </DialogModal>
  );
}
